import { Injectable } from '@angular/core';
import { UrlSegment } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { KeycloakClientService } from 'app/core/auth/services/keycloak-client.service';
import { UserDetailsService } from 'app/core/services/user-details.service';
import { cloneDeep } from 'lodash-es';
import { firstValueFrom, Observable, of, switchMap, take } from 'rxjs';
import { ConfigurationService } from '../config/configuration.service';
import { AppPermissions } from '../data/app-permissions';
import { AppPermissionItem } from '../models/app-permission-item.model';
import { UserDetails } from '../models/auth/user-details.model';
import { UriUtils } from '../utils/uri.utils';
import { InstanceActiveService } from './instance-active.service';
import { LoggerService } from './logger.service';
import { StorageService } from './storage.service';

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    constructor(
        private userDetailsService: UserDetailsService,
        private instanceActiveService: InstanceActiveService,
        private configurationService: ConfigurationService,
        private logger: LoggerService,
        private storageService: StorageService,
        private translocoService: TranslocoService,
        private keycloakClient: KeycloakClientService
    ) {}

    async loadCurrentUser(): Promise<boolean> {
        if (!this.keycloakClient.isLoggedIn()) {
            return false;
        }

        return firstValueFrom(
            this.userDetailsService.get().pipe(
                switchMap(userDetails => {
                    this.userDetailsService.user = userDetails;
                    this.instanceActiveService.setInstanceFromUser(userDetails.instanceContext);
                    this.setActiveLang(userDetails);
                    return of(!!userDetails);
                }),
                take(1)
            )
        );
    }

    getPermissionsForUrl(url: string, segments?: UrlSegment[]): string[] {
        let permissions = [];

        try {
            const paths = UriUtils.extractPaths(url, segments);
            const lastPath = paths.slice(-1)[0];

            let parents = cloneDeep(AppPermissions);

            for (const path of paths) {
                const current = parents.find(permissionItem => permissionItem.segment === path);

                if (!current) {
                    break;
                }

                if (lastPath === path || !current.children?.length) {
                    if (current.permissions) {
                        permissions.push(...current.permissions);
                    } else if (current.children) {
                        permissions.push(...this.childPermission(current.children));
                    }
                }
                parents = current.children;
            }

            permissions = Array.from(new Set(permissions));
        } catch (e) {
            this.logger.error(
                `getPermissionFromUrl => Error getting permission for route ${url}. Something it's wrong, please review`,
                e
            );
        }

        return permissions;
    }

    canAccessUrl(url: string): Observable<boolean> {
        const permissions = this.getPermissionsForUrl(url);

        if (permissions.length) {
            return of(this.userDetailsService.hasAnyPermission(...permissions));
        }

        return of(true);
    }

    private childPermission(children: AppPermissionItem[]): string[] {
        let permissions = [];
        children.forEach(child => {
            if (child.permissions && child.permissions.length) {
                permissions.push(...child.permissions);
            }
            if (child.children) {
                permissions.push(...this.childPermission(child.children));
            }
        });

        return permissions;
    }

    private setActiveLang(userDetails: UserDetails): void {
        const lang =
            this.storageService.getLanguage() ??
            userDetails.lang ??
            this.configurationService.getConfig().langDefault;
        this.translocoService.setActiveLang(lang);
    }
}
